/* Collection-scoped finder.

   Searches ONLY the collection you are standing in. It never queries the
   store, so a collection search can't leak results from outside it.

   Two indexes, both read from markup Shopify already rendered on the page,
   so this costs no extra network request:

   1. Facet values in the filter rail - APC RBC numbers, compatible UPS
      models, brands, capacities. Picking one ticks the real checkbox and
      submits the existing filter form, so the grid, the match count and the
      pagination all stay server-rendered and correct.
   2. Product cards on the current page - a direct jump to the product.

   Anything with no match among the visible page products or available
   filters falls through to a clearly labelled store-wide search.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-upsb-col-search]');
  if (!root) return;

  var input = root.querySelector('.upsb-col-search__input');
  var results = root.querySelector('.upsb-col-search__results');
  var clearBtn = root.querySelector('.upsb-col-search__clear');
  if (!input || !results) return;

  var d = results.dataset;
  var L = {
    refine: d.labelRefine || 'Narrow this collection',
    products: d.labelProducts || 'Products on this page',
    nomatch: d.labelNomatch || 'No match in the available filters or products on this page',
    store: d.labelStore || 'Search the whole store for TERM'
  };
  var searchUrl = d.searchUrl || '/search';
  var MAX = 6;

  var index = null;
  var options = [];
  var active = -1;

  function norm(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function buildIndex() {
    var facets = [];
    var form = document.querySelector('.upsb-col-filter-form');

    if (form) {
      Array.prototype.forEach.call(
        form.querySelectorAll('.upsb-col-facet-option'),
        function (opt) {
          var box = opt.querySelector('input[type="checkbox"]');
          var labelEl = opt.querySelector('.upsb-col-facet-option__label');
          if (!box || !labelEl || box.disabled) return;

          var label = labelEl.textContent.trim();
          if (!label) return;

          var group = '';
          var details = opt.closest('details');
          if (details) {
            var sum = details.querySelector('.upsb-col-filter-group__summary span');
            if (sum) group = sum.textContent.trim();
          }

          var countEl = opt.querySelector('.upsb-col-facet-option__count');

          facets.push({
            kind: 'facet',
            label: label,
            group: group,
            meta: countEl ? countEl.textContent.trim() : '',
            box: box,
            dedupe: norm(label),
            key: norm(label)
          });
        }
      );
    }

    var byHref = {};
    Array.prototype.forEach.call(
      document.querySelectorAll('.upsb-col-products .upsb-card-product'),
      function (card) {
        var best = null;
        Array.prototype.forEach.call(
          card.querySelectorAll('a[href*="/products/"]'),
          function (a) {
            var t = (a.textContent || '').replace(/\s+/g, ' ').trim();
            if (!best || t.length > best.text.length) {
              best = { href: a.getAttribute('href'), text: t };
            }
          }
        );
        if (!best || !best.href || best.text.length < 3) return;
        if (byHref[best.href]) return;

        byHref[best.href] = {
          kind: 'product',
          label: best.text,
          href: best.href,
          meta: '',
          dedupe: best.href,
          key: norm(card.textContent)
        };
      }
    );

    var products = Object.keys(byHref).map(function (h) {
      return byHref[h];
    });

    return { facets: facets, products: products };
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function highlight(label, raw) {
    var safe = esc(label);
    if (!raw) return safe;
    var i = label.toLowerCase().indexOf(raw.toLowerCase());
    if (i === -1) return safe;
    return (
      esc(label.slice(0, i)) +
      '<mark>' +
      esc(label.slice(i, i + raw.length)) +
      '</mark>' +
      esc(label.slice(i + raw.length))
    );
  }

  /* Rank exact label matches first, then labels that start with the term,
     then anything else that contains it. One row per distinct value: the
     same cartridge number can appear under three different filters, and
     three identical-looking rows is noise, not choice. */
  function matches(list, needle) {
    var seen = {};
    var exact = [];
    var starts = [];
    var rest = [];

    for (var i = 0; i < list.length; i++) {
      var it = list[i];
      if (it.key.indexOf(needle) === -1) continue;
      if (seen[it.dedupe]) continue;
      seen[it.dedupe] = true;

      var lk = norm(it.label);
      if (lk === needle) exact.push(it);
      else if (lk.indexOf(needle) === 0) starts.push(it);
      else rest.push(it);
    }

    return exact.concat(starts, rest).slice(0, MAX);
  }

  // 16 Oct 2026 (code audit #21): each option gets an id and the input names the highlighted one
  // (aria-activedescendant), so a screen reader announces the option the arrow keys move to.
  var idBase = (results.id || 'upsb-col-search-results') + '-opt-';
  function optId(i) {
    return idBase + i;
  }

  function close() {
    results.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    options = [];
    active = -1;
  }

  function render(raw) {
    var needle = norm(raw);
    if (!needle) {
      close();
      return;
    }
    if (!index) index = buildIndex();

    var facetHits = matches(index.facets, needle);
    var productHits = matches(index.products, needle);

    var html = '';
    options = [];

    if (facetHits.length) {
      html += '<div class="upsb-col-search__group">' + esc(L.refine) + '</div>';
      facetHits.forEach(function (hit) {
        var label = hit.group ? hit.group + ': ' + hit.label : hit.label;
        html +=
          '<button type="button" class="upsb-col-search__option" role="option" aria-selected="false" id="' +
          optId(options.length) +
          '" data-i="' +
          options.length +
          '"><span class="upsb-col-search__option-label">' +
          highlight(label, raw) +
          '</span>' +
          (hit.meta ? '<span class="upsb-col-search__option-meta">' + esc(hit.meta) + '</span>' : '') +
          '</button>';
        options.push(hit);
      });
    }

    if (productHits.length) {
      html += '<div class="upsb-col-search__group">' + esc(L.products) + '</div>';
      productHits.forEach(function (hit) {
        html +=
          '<a class="upsb-col-search__option" role="option" aria-selected="false" id="' +
          optId(options.length) +
          '" data-i="' +
          options.length +
          '" href="' +
          esc(hit.href) +
          '"><span class="upsb-col-search__option-label">' +
          highlight(hit.label, raw) +
          '</span></a>';
        options.push(hit);
      });
    }

    if (!options.length) {
      html += '<div class="upsb-col-search__empty">' + esc(L.nomatch) + '</div>';
    }

    var storeHref = searchUrl + '?q=' + encodeURIComponent(raw) + '&type=product';
    html +=
      '<div class="upsb-col-search__fallback"><a class="upsb-col-search__option" role="option" aria-selected="false" id="' +
      optId(options.length) +
      '" data-i="' +
      options.length +
      '" href="' +
      esc(storeHref) +
      '"><span class="upsb-col-search__option-label">' +
      esc(L.store.replace('TERM', raw)) +
      '</span></a></div>';
    options.push({ kind: 'store', href: storeHref });

    results.innerHTML = html;
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  }

  function setActive(i) {
    var nodes = results.querySelectorAll('.upsb-col-search__option');
    if (!nodes.length) return;
    if (i < 0) i = nodes.length - 1;
    if (i >= nodes.length) i = 0;
    Array.prototype.forEach.call(nodes, function (n) {
      n.setAttribute('aria-selected', 'false');
    });
    nodes[i].setAttribute('aria-selected', 'true');
    input.setAttribute('aria-activedescendant', nodes[i].id);
    nodes[i].scrollIntoView({ block: 'nearest' });
    active = i;
  }

  function choose(i) {
    var hit = options[i];
    if (!hit) return;
    if (hit.kind === 'facet') {
      hit.box.checked = true;
      var form = hit.box.form || document.querySelector('.upsb-col-filter-form');
      if (form) form.submit();
      return;
    }
    if (hit.href) window.location.href = hit.href;
  }

  var timer = null;
  input.addEventListener('input', function () {
    if (clearBtn) clearBtn.hidden = input.value === '';
    clearTimeout(timer);
    timer = setTimeout(function () {
      render(input.value.trim());
    }, 120);
  });

  input.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (results.hidden) render(input.value.trim());
      else setActive(active + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(active - 1);
    } else if (e.key === 'Enter') {
      if (results.hidden) return;
      e.preventDefault();
      choose(active >= 0 ? active : 0);
    } else if (e.key === 'Escape') {
      close();
      input.blur();
    }
  });

  results.addEventListener('click', function (e) {
    var el = e.target.closest('.upsb-col-search__option');
    if (!el) return;
    var i = parseInt(el.getAttribute('data-i'), 10);
    if (options[i] && options[i].kind === 'facet') {
      e.preventDefault();
      choose(i);
    }
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      input.value = '';
      clearBtn.hidden = true;
      close();
      input.focus();
    });
  }

  document.addEventListener('click', function (e) {
    if (!root.contains(e.target)) close();
  });
})();